/**
 * Cards — каталог «Выберите карту» (Flow step="cards"). Три состояния экрана:
 * 1) список карт (изображение из админки + цена выпуска и курс поверх нижней полосы);
 * 2) подтверждение выпуска выбранной карты — со списком запрещённых операций, его нельзя
 *    пропустить (ТЗ), и списанием стоимости с рублёвого счёта (ноль → «Бесплатно»);
 * 3) анимация выпуска: карта выплывает из глубины, разворачивается лицом, проходит изумрудная
 *    вспышка, проявляются «•• 8075».
 * coming_soon → картинка приглушается + «Скоро», кнопки выпуска нет. Карта может быть только
 * одна: если она уже выпущена, каталог показывает это и выпуск недоступен.
 * Пропсы: onBack, onIssued (карта выпущена — обновить кабинет и уйти на экран карты).
 */
import { useEffect, useState } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { Screen } from "@/components/Screen";
import { Spinner } from "@/components/Spinner";
import { Note } from "@/components/Note";
import { ApiError, cardsApi, friendlyError, type CatalogCard, type IssuedCard } from "@/lib/api";
import { CardFace } from "./CardFace";
import { RestrictionsBlock } from "./restrictions";

type Props = { onBack: () => void; onIssued?: (card: IssuedCard) => void };

function fmtRub(n: number | null): string {
  if (n == null) return "—";
  return n <= 0 ? "Бесплатно" : `${Number(n).toLocaleString("ru-RU")} ₽`;
}

function CardPanel({ card, issued, onIssue }: { card: CatalogCard; issued: boolean; onIssue: () => void }) {
  const soon = card.coming_soon;
  return (
    <div className="space-y-3">
      {/* Карта — родные пропорции (без обрезки дизайна). Инфо — в нижней затемнённой
          полосе прямо на карте; верх карты (логотип/арт) остаётся чистым. */}
      <div
        className="relative overflow-hidden rounded-2xl border border-white/[0.08] shadow-xl shadow-black/40"
        style={{ aspectRatio: "1.586" }}
      >
        {card.image ? (
          <img
            src={card.image}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover ${soon ? "blur-[3px] brightness-[0.55]" : ""}`}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-600 to-surface-base" />
        )}

        {/* Нижняя полоса с инфо (градиент-скрим для читаемости поверх любого дизайна) */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-4 pb-3.5 pt-10 text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.7)]">
          <div className="flex items-center gap-2">
            <span className="truncate text-[17px] font-semibold">{card.name}</span>
            <span className="shrink-0 rounded-md bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide">
              {card.currency}
            </span>
            {card.best_rate && <span className="ml-auto shrink-0 text-[12px] font-medium text-brand-400">👑 Лучший курс</span>}
          </div>
          {card.description && <div className="mt-0.5 truncate text-[12px] text-white/70">{card.description}</div>}

          <div className="mt-3 flex items-end justify-between gap-3">
            <div>
              <div className="text-[16px] font-semibold leading-none">{fmtRub(card.cost_rub)}</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-white/55">Стоимость открытия</div>
            </div>
            <div className="text-right">
              <div className="text-[16px] font-semibold leading-none">
                {card.exchange_rate != null ? `$1 = ${Number(card.exchange_rate)} ₽` : "—"}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-white/55">Курс обмена</div>
            </div>
          </div>
        </div>

        {/* «Скоро» — по центру поверх приглушённой карты */}
        {soon && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="rounded-full border border-white/20 bg-black/55 px-5 py-2 text-[16px] font-semibold tracking-wide text-white backdrop-blur-sm">
              Скоро
            </span>
          </div>
        )}
      </div>

      {/* Выпустить — только у доступных карт и пока карта не выпущена (она может быть одна) */}
      {!soon && !issued && (
        <button
          onClick={onIssue}
          className="w-full rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95"
        >
          Выпустить карту
        </button>
      )}
    </div>
  );
}

/** Экран подтверждения: цена, запрещённые операции и кнопка выпуска. */
function Confirm({
  card,
  balanceRub,
  submitting,
  error,
  onIssue,
  onCancel,
}: {
  card: CatalogCard;
  balanceRub: number;
  submitting: boolean;
  error: string | null;
  onIssue: () => void;
  onCancel: () => void;
}) {
  const cost = card.cost_rub ?? 0;
  const enough = cost <= 0 || balanceRub >= cost;
  return (
    <Screen title="Выпуск карты" subtitle={card.name}>
      <div className="space-y-4">
        <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3.5">
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-white/55">Стоимость выпуска</span>
            <span className="text-[15px] font-semibold">{fmtRub(card.cost_rub)}</span>
          </div>
          {cost > 0 && (
            <div className="mt-2 flex items-center justify-between border-t border-white/[0.06] pt-2">
              <span className="text-[14px] text-white/55">Спишется со счёта</span>
              <span className="text-[15px] font-medium tabular-nums">
                {balanceRub.toLocaleString("ru-RU")} ₽ → {(balanceRub - cost).toLocaleString("ru-RU")} ₽
              </span>
            </div>
          )}
        </div>

        <RestrictionsBlock />

        {!enough && (
          <Note variant="warning">
            На счёте не хватает {(cost - balanceRub).toLocaleString("ru-RU")} ₽. Пополните счёт и
            вернитесь к выпуску.
          </Note>
        )}
        {error && <Note variant="danger">{error}</Note>}

        <button
          onClick={onIssue}
          disabled={!enough || submitting}
          className="w-full rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95 disabled:opacity-40"
        >
          {submitting ? "Выпускаем…" : "Выпустить карту"}
        </button>
        <button onClick={onCancel} className="w-full py-2 text-[14px] text-white/45 active:opacity-70">
          Отмена
        </button>
      </div>
    </Screen>
  );
}

/** Анимация выпуска: карта разворачивается лицом, по ней проходит вспышка, проявляются цифры. */
function IssueAnimation({ card, onDone }: { card: IssuedCard; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <Screen>
      <div className="flex min-h-[70dvh] flex-col items-center justify-center">
        <div className="animate-card-issue relative w-full max-w-[22rem]">
          <CardFace card={card} glow />
          {/* вспышка поверх карты */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[26px]">
            <div className="animate-card-sheen absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/45 to-transparent" />
          </div>
          {/* цифры проявляются поверх «родных» — те же координаты, что в CardFace */}
          <div className="animate-card-digits pointer-events-none absolute bottom-5 left-5 flex items-center gap-2 text-[17px] font-medium tabular-nums text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.6)]">
            <span className="tracking-[0.2em]">••</span>
            <span>{card.last4}</span>
          </div>
        </div>
        <div className="mt-8 text-center">
          <div className="text-[17px] font-semibold">Карта выпущена</div>
          <div className="mt-1 text-[13px] text-white/45">Пополняйте её с внутреннего счёта</div>
        </div>
      </div>
    </Screen>
  );
}

export function Cards({ onBack, onIssued }: Props) {
  const [cards, setCards] = useState<CatalogCard[] | null>(null);
  const [issued, setIssued] = useState(false);
  const [balanceRub, setBalanceRub] = useState(0);
  const [confirming, setConfirming] = useState<CatalogCard | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issuedCard, setIssuedCard] = useState<IssuedCard | null>(null);

  useEffect(() => {
    cardsApi
      .list()
      .then((r) => {
        setCards(r.cards);
        setIssued(r.issued);
      })
      .catch(() => setCards([]));
    cardsApi
      .my()
      .then((r) => setBalanceRub(r.account_rub))
      .catch(() => undefined);
  }, []);

  useBackButton(() => (confirming ? setConfirming(null) : onBack()));

  const issue = async () => {
    if (!confirming || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await cardsApi.issue(confirming.id);
      setIssuedCard(r.card);
      setIssued(true);
      setConfirming(null);
    } catch (e) {
      if (e instanceof ApiError && e.detail === "insufficient_rub") {
        setError("На счёте недостаточно рублей для выпуска.");
      } else if (e instanceof ApiError && e.detail === "already_issued") {
        setError("Карта уже выпущена — она может быть только одна.");
      } else if (e instanceof ApiError && (e.detail === "coming_soon" || e.detail === "design_unavailable")) {
        setError("Эта карта пока недоступна к выпуску.");
      } else {
        setError(friendlyError(e, "Не удалось выпустить карту"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (issuedCard) {
    return <IssueAnimation card={issuedCard} onDone={() => onIssued?.(issuedCard)} />;
  }

  if (confirming) {
    return (
      <Confirm
        card={confirming}
        balanceRub={balanceRub}
        submitting={submitting}
        error={error}
        onIssue={issue}
        onCancel={() => setConfirming(null)}
      />
    );
  }

  return (
    <Screen
      title="Выберите карту"
      subtitle={issued ? "Карта уже выпущена — она может быть только одна." : "Виртуальная карта в долларах: пополняется со счёта."}
    >
      {cards === null ? (
        <div className="mt-10 flex justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      ) : cards.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-white/[0.06] bg-surface-card px-4 py-12 text-center">
          <div className="text-3xl">💳</div>
          <div className="mt-3 text-[15px] font-medium">Выпуск карт пока недоступен</div>
          <div className="mx-auto mt-1 max-w-[17rem] text-[13px] text-white/45">
            Мы готовим карты для оплаты за границей. Загляните позже.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {cards.map((c) => (
            <CardPanel
              key={c.id}
              card={c}
              issued={issued}
              onIssue={() => {
                setError(null);
                setConfirming(c);
              }}
            />
          ))}
        </div>
      )}
    </Screen>
  );
}
